import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { shift } from '@floating-ui/react';
import { enGB } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { DATE_PICKER_FORMAT } from '../../utils/dateUtils';

registerLocale('en-GB', enGB);

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const YEAR_RANGE_START = 1950;
const YEAR_RANGE_END_OFFSET = 10;

/**
 * Custom dropdown for inside the calendar header.
 * Replaces native <select> with a styled dropdown matching PillSelect's
 * visual language — rounded-xl panel, shadow-lg, theme-aware highlight.
 * Compact trigger (~32px) appropriate for the calendar popup context.
 */
const CalendarSelect = ({ value, onChange, options, isAdmin, ariaLabel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const selected = listRef.current.querySelector('[data-selected="true"]');
      if (selected) selected.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen]);

  const select = useCallback((val) => {
    onChange(val);
    setIsOpen(false);
  }, [onChange]);

  const selected = options.find(o => o.value === value);
  const highlightClass = isAdmin
    ? 'bg-red-50 text-red-600 font-medium'
    : 'bg-blue-50 text-blue-700 font-medium';

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className={`calendar-select-trigger${isAdmin ? ' admin' : ' employee'}`}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
      >
        <span className="truncate">{selected?.label || ''}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="calendar-select-dropdown" ref={listRef}>
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              data-selected={option.value === value}
              onClick={() => select(option.value)}
              className={`calendar-select-option ${
                option.value === value ? highlightClass : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Standardized DatePicker wrapper.
 *
 * - Custom header: prev/next pinned to the edges, month+year grouped in
 *   the centre. Two tasks (step vs jump) stay spatially distinct.
 * - Uses a readOnly customInput to suppress the iOS virtual keyboard while
 *   keeping the calendar popup functional. The readOnly is on the HTML input
 *   (not the react-datepicker prop), so the popup still opens on tap.
 * - Renders the Calendar icon + relative wrapper by default (DS standard).
 * - Pass `showIcon={false}` for compact/inline-form contexts where the icon doesn't fit.
 * - Pass `inline` for calendar-only mode (no input rendered).
 * - By default renders the popper into `#datepicker-portal` with high z-index so the calendar
 *   is not clipped inside modals (`.modal-container` overflow). Override with `portalId` / `popperClassName`.
 * - All other react-datepicker props are forwarded unchanged; `popperProps` merges with `{ strategy: 'fixed' }`.
 * - react-datepicker v8 uses Floating-UI (not Popper.js). `popperModifiers` must be Floating-UI
 *   Middleware[] (e.g. `shift()`, `flip()`, `offset()`), NOT Popper.js-format objects.
 */
const ReadOnlyInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} readOnly />
));
ReadOnlyInput.displayName = 'ReadOnlyInput';

const DatePickerField = ({
  theme = 'admin',
  dateFormat = DATE_PICKER_FORMAT,
  locale = 'en-GB',
  className = '',
  wrapperClassName = 'w-full',
  showIcon = true,
  inline,
  ...datePickerProps
}) => {
  const {
    popperProps: userPopperProps,
    portalId: userPortalId,
    popperClassName: userPopperClassName,
    popperModifiers: userPopperModifiers,
    ...restDatePickerProps
  } = datePickerProps;

  const isAdmin = theme === 'admin';
  const calendarThemeClass = isAdmin ? 'datepicker-theme-admin' : 'datepicker-theme-employee';

  const monthOptions = useMemo(() =>
    MONTHS.map((label, i) => ({ value: i, label })), []
  );

  const yearOptions = useMemo(() => {
    const end = new Date().getFullYear() + YEAR_RANGE_END_OFFSET;
    return Array.from({ length: end - YEAR_RANGE_START + 1 }, (_, i) => {
      const y = YEAR_RANGE_START + i;
      return { value: y, label: String(y) };
    });
  }, []);

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="datepicker-custom-header">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className={`datepicker-nav-btn${isAdmin ? ' admin' : ' employee'}`}
        aria-label="Previous month"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div className="datepicker-custom-header-selects" role="group" aria-label="Month and year">
        <CalendarSelect
          value={date.getMonth()}
          onChange={changeMonth}
          options={monthOptions}
          isAdmin={isAdmin}
          ariaLabel="Select month"
        />
        <CalendarSelect
          value={date.getFullYear()}
          onChange={changeYear}
          options={yearOptions}
          isAdmin={isAdmin}
          ariaLabel="Select year"
        />
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className={`datepicker-nav-btn${isAdmin ? ' admin' : ' employee'}`}
        aria-label="Next month"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  const sharedProps = {
    dateFormat,
    locale,
    renderCustomHeader,
    calendarClassName: calendarThemeClass,
    portalId: userPortalId ?? 'datepicker-portal',
    popperClassName: userPopperClassName ?? 'datepicker-popper-high-z',
    popperProps: {
      strategy: 'fixed',
      ...userPopperProps,
    },
    popperModifiers: [
      shift({ padding: 8 }),
      ...(userPopperModifiers || []),
    ],
  };

  if (inline) {
    return (
      <DatePicker
        inline
        {...sharedProps}
        {...restDatePickerProps}
      />
    );
  }

  const themeClass = theme === 'admin' ? 'input-admin' : 'input';
  const iconColor = theme === 'admin' ? 'text-red-600' : 'text-blue-800';
  // Icon is absolutely positioned at left-3. Text MUST start at pl-12 or the
  // placeholder/value paints under the calendar glyph (common footgun when
  // callers pass className and wipe the default). Preserve an explicit pl-*
  // if the caller already set one (e.g. pl-10 in compact layouts).
  const callerHasLeadingPad = /\bpl-\d+\b/.test(className);
  const iconPad = showIcon && !callerHasLeadingPad ? 'pl-12' : '';
  const resolvedClassName = className
    ? `${className} ${iconPad}`.trim()
    : `${themeClass} ${showIcon ? 'pl-12 pr-3' : ''} text-sm w-full`.replace(/\s+/g, ' ').trim();

  if (!showIcon) {
    return (
      <DatePicker
        customInput={<ReadOnlyInput />}
        {...sharedProps}
        className={resolvedClassName}
        wrapperClassName={wrapperClassName}
        {...restDatePickerProps}
      />
    );
  }

  return (
    <div className="relative">
      <Calendar
        className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${iconColor} pointer-events-none z-10`}
      />
      <DatePicker
        customInput={<ReadOnlyInput />}
        {...sharedProps}
        className={resolvedClassName}
        wrapperClassName={wrapperClassName}
        {...restDatePickerProps}
      />
    </div>
  );
};

export default DatePickerField;
